'use client'

import { Laptop, Moon, Sun } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { Menu, MenuPopup, MenuRadioGroup, MenuRadioItem, MenuTrigger } from '~/components/ui/menu'
import { useTheme, type Theme } from '~/hooks/use-theme'

const THEME_OPTIONS: { value: Theme; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Jasny', Icon: Sun },
  { value: 'dark', label: 'Ciemny', Icon: Moon },
  { value: 'system', label: 'Systemowy', Icon: Laptop },
]

/**
 * The only consumer of `useTheme` — it neither re-implements the storage logic
 * nor introduces a second storage key. Rendered in both the desktop header and
 * the mobile navigation, though `useIsMobile` means only one is ever mounted.
 */
export function ThemeToggle() {
  const { theme, resolvedTheme, setTheme } = useTheme()

  const CurrentIcon = resolvedTheme === 'dark' ? Moon : Sun

  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            aria-label="Wybierz motyw wyświetlania"
            size="icon"
            type="button"
            variant="outline"
          >
            <CurrentIcon aria-hidden="true" />
          </Button>
        }
      />
      {/* The trigger is an icon button, so `--anchor-width` is one button wide
          and the popup inherits nothing useful from it. Three short words plus
          an icon and a tick need more than that, and without a floor the
          longest of them ("Systemowy") sets the width on its own. */}
      <MenuPopup align="end" className="min-w-44">
        <MenuRadioGroup onValueChange={(value) => setTheme(value as Theme)} value={theme}>
          {THEME_OPTIONS.map(({ value, label, Icon }) => (
            <MenuRadioItem closeOnClick key={value} value={value}>
              {/* ONE child, not two. `MenuRadioItem` is a two-column grid —
                  the selection tick, then the content — so a second child is
                  auto-placed into a second ROW, which put every icon on a line
                  of its own above its label. Pairing them here keeps the
                  primitive's contract (one content cell) intact for its other
                  callers. */}
              <span className="flex items-center gap-2">
                <Icon aria-hidden="true" />
                {label}
              </span>
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuPopup>
    </Menu>
  )
}
